import * as Yup from "yup";
import { getUniqueElements, isValidNumber } from "./helpfulFunctions";

export const IMAGE_FILE_SIZE = 5000 * 1024;
export const IMAGE_SUPPORTED_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/png",
  "image/webm",
];

export const isValidEmail = () =>
  Yup.string()
    .email("Invalid email value")
    .matches(/^[A-z+0-9+@+.]+$/, "Invalid email value");

export const isValidDomain = () =>
  Yup.string().matches(
    /^(https?:\/\/)?[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,6}$/i,
    "Invalid domain, example https://domain-name.com, http://domainname.com, domainname.com, stage1.domain-name.com"
  );

export const isValidSlug = () =>
  Yup.string()
    .max(60, "Slug must be 60 characters or less")
    .matches(
      /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/,
      "Only lowercase Latin letters,(0-9),(-_)"
    );

export const isValidBrandSlug = () =>
  Yup.string()
    .max(60, "Slug must be 60 characters or less")
    .matches(
      /^[.]?[a-z0-9]+(?:[-_.][a-z0-9]+)*[.]?$/,
      "Only lowercase Latin letters,(0-9),(-_),(one . in a row)"
    );

export const isValidUrl = () =>
  Yup.string().matches(
    /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\\+.~#?&/=]*)$/,
    "Invalid link, example https://domain.com/path/etc"
  );

export const isNotEmptyObj = (msg = "") =>
  Yup.object().test(
    "empty-check",
    msg || "This field can't be empty",
    (field) => {
      const firstValue = field ? Object.values(field)[0] : undefined;
      const objVal: string = typeof firstValue === "string" ? firstValue : "";
      return !!objVal?.length;
    }
  );

export const isValidPassword = () =>
  Yup.string()
    .min(8, "Password must be longer")
    .max(30, "Password must be shorter")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,}$/,
      "Password must include at least one number, lowercase and uppercase characters"
    );

export const isValidImageFile = () =>
  Yup.mixed()
    .test("fileFormat", "Unsupported format", (value) =>
      value && value[0] ? IMAGE_SUPPORTED_FORMATS.includes(value[0].type) : true
    )
    .test("fileSize", "File size too large", (value) =>
      value && value[0] ? value[0].size <= IMAGE_FILE_SIZE : true
    );

export const jsonErrorMessage = "Value must be valid JSON.";

export const isValidJSON = () =>
  Yup.mixed().test("type", jsonErrorMessage, (value: any) => {
    try {
      JSON.parse(value);
    } catch (e) {
      return !value;
    }
    return (
      typeof JSON.parse(value) === "object" &&
      isValidJsonWithoutDuplicates(value)
    );
  });

export const isValidRuleDisplayCondition = ({ countries, languages }) =>
  Yup.object().shape({
    user_state: Yup.string().nullable(),
    country: Yup.array().test("invalid-country", "Invalid country", (value) => {
      return !getUniqueElements(value, countries)?.length;
    }),
    locale: Yup.array().test("invalid-locale", "Invalid locale", (value) => {
      return !getUniqueElements(value, languages)?.length;
    }),
    device_platform: Yup.array().of(Yup.string()).nullable(),
  });

export const isValidDisplayCondition = ({ countries, languages }) =>
  Yup.object().shape({
    allow: isValidRuleDisplayCondition({ countries, languages }),
    deny: isValidRuleDisplayCondition({ countries, languages }),
  });

export const isRequiredWithOther = (
  otherFieldTitle: string,
  otherFieldName: string
) =>
  Yup.string()
    .nullable()
    .test("required", `Required if ${otherFieldTitle} is set`, (value, ctx) =>
      !value ? !ctx?.parent?.[otherFieldName] : true
    );

export const isValidOptionalInterval = () =>
  Yup.string()
    .nullable()
    .test(
      "interval-complete",
      "Please fill in both the value and the unit",
      (value) => {
        if (!value) {
          return true;
        }

        const [claimable_number, claimable_period] = value.split(" ");
        return !!claimable_number && !!claimable_period;
      }
    )
    .test("interval-positive", "Value must be a positive number", (value) => {
      if (!value) {
        return true;
      }

      const [claimable_number] = value.split(" ");
      return isValidNumber(claimable_number) && +claimable_number > 0;
    });

export const isValidBonusCode = (
  firstFieldName: string,
  secondFieldName: string
) =>
  Yup.string()
    .nullable()
    .test(
      "required",
      "Required if Bonus plan ID and Amount are not set",
      (value, ctx) =>
        !!value ||
        !(!ctx?.parent?.[firstFieldName] && !ctx?.parent?.[secondFieldName])
    )
    .test(
      "other-empty",
      "Bonus Code or Bonus plan ID with Amount can be set",
      (value, ctx) =>
        value
          ? !ctx?.parent?.[firstFieldName] && !ctx?.parent?.[secondFieldName]
          : true
    );

export const isRequiredString = (fieldName: string) =>
  Yup.string()
    .required(`${fieldName} is required`)
    .typeError(`${fieldName} must be a string`);

export const isValidJsonWithoutDuplicates = (jsonString) => {
  const scopeStack = [new Set()];
  const tokenRegex = /([{} [\]])|"(?:\\.|[^"\\])*"\s*:|"(?:\\.|[^"\\])*"/g;
  let match;

  while ((match = tokenRegex.exec(jsonString)) !== null) {
    const token = match[0];

    if (token === "{") {
      scopeStack.push(new Set());
    } else if (token === "}") {
      if (scopeStack.length > 1) {
        scopeStack.pop();
      }
    } else if (token.endsWith(":")) {
      const keyName = token.match(/"([^"\\]*(?:\\.[^"\\]*)*)"/)?.[1];

      if (keyName !== undefined) {
        const currentScope = scopeStack[scopeStack.length - 1];

        if (currentScope.has(keyName)) {
          return false;
        }

        currentScope.add(keyName);
      }
    }
  }

  return true;
};
